import { normalizeClassId } from '../../../shared/rules/classIdentity.mjs';
import { countClassExpertise } from '../../../shared/rules/classExpertise';
import { FIGHTING_STYLE_CLASSES, getFightingStyleOptions } from '../../../shared/rules/fightingStyle.mjs';
import { LANGUAGE_OPTIONS, getCharacterLanguages } from '../../../shared/rules/languages.mjs';
import { getClassSpellLimits } from '../../../shared/rules/spellPreparation';
import { DIVINE_ORDERS } from '../../../shared/rules/divineOrder';
import { PRIMAL_ORDERS } from '../../../shared/rules/primalOrder';
import { SPEAK_WITH_ANIMALS_SPELL_ID } from '../../../shared/rules/druid.mjs';
import { MAGIC_INITIATE_ABILITIES, MAGIC_INITIATE_CANTRIP_COUNT, MAGIC_INITIATE_LISTS } from '../../../shared/rules/magicInitiate.mjs';
import { getWeaponMasteryEntitlements, normalizeWeaponKey } from './weaponMastery';
import { SKILLS } from '../skillSchema';

// Level 1 choices collected by the creation wizard (2024 / SRD 5.2.1). The rules come from the existing
// authorities; each choice is saved after the character exists through the same route the sheet uses.

const proficientKeys = (skills) => Object.entries(skills || {}).filter(([, value]) => value?.proficient).map(([key]) => key);
const uniqueFilled = (values = []) => [...new Set((Array.isArray(values) ? values : []).filter(Boolean))];
export const optionId = (option) => (typeof option === 'string' ? option : option?.spellId || option?.key || option?.id);
/** The picks still offered by a requirement; picks left over from an earlier class or species are dropped. */
export const validPicks = (picks, requirement) => {
  if (!requirement) return [];
  const ids = new Set(requirement.options.map(optionId));
  return uniqueFilled(picks).filter((pick) => ids.has(pick)).slice(0, requirement.count);
};
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Magic Initiate lists the new character gets from its origin: the background's feat, and a Human's Versatile feat.
 * Each asks for its spellcasting ability, two cantrips and a level 1 spell from that list.
 */
const magicInitiateLists = (form) => [...new Set([
  form.background?.feat?.featName === 'Magic Initiate' ? form.background.feat.spellList : null,
  form.versatileFeat === 'Magic Initiate' ? form.versatileSpellList : null,
].filter((list) => MAGIC_INITIATE_LISTS[list]))];
const magicInitiatePicks = (form, list) => form.magicInitiatePicks?.[list] || {};

/** What a level 1 character of this species, background and class has to choose, given the loaded catalogues. */
export function levelOneRequirements(form = {}, occupation, { weapons = {}, spells = {}, status = 'ready' } = {}) {
  const classId = normalizeClassId(occupation?.name);
  const levelOne = [{ Name: occupation?.name || '', Level: 1 }];
  const backgroundSkills = proficientKeys(form.background?.skills);
  const speciesFixed = proficientKeys(form.race?.skills);
  const speciesChoice = form.race?.skillChoices?.count ? {
    count: form.race.skillChoices.count,
    options: (form.race.skillChoices.options || []).filter((key) => !backgroundSkills.includes(key) && !speciesFixed.includes(key)),
  } : null;
  const speciesPicks = validPicks(form.speciesSkills, speciesChoice);
  const granted = [...new Set([...backgroundSkills, ...speciesFixed, ...speciesPicks])];
  const classSkillData = occupation?.proficiencies?.skills;
  const classSkills = classSkillData?.count ? {
    count: classSkillData.count,
    options: (classSkillData.options || []).filter((key) => !granted.includes(key)),
  } : null;
  const classPicks = validPicks(form.classSkills, classSkills);
  const expertiseCount = countClassExpertise(levelOne);
  const pseudo = { occupation: levelOne, race: form.race, background: form.background, languages: ['Common', ...(form.originLanguages || [])] };
  const known = getCharacterLanguages(pseudo, { includeRogueChoice: false });
  const mastery = getWeaponMasteryEntitlements(pseudo, weapons).find((entry) => entry.sourceId === classId) || null;
  const catalogSpells = Object.entries(spells || {}).map(([spellId, spell]) => ({ ...spell, spellId: spell.spellId || spellId }));
  const onList = (spell, list) => (spell.classes || []).map((name) => String(name).toLowerCase()).includes(list);
  const limits = getClassSpellLimits(classId, 1, { classState: { cleric: { divineOrder: { id: form.divineOrderId } }, druid: { primalOrder: { id: form.primalOrderId } } } });
  const classSpells = Object.entries(spells || {}).map(([spellId, spell]) => ({ ...spell, spellId: spell.spellId || spellId }))
    .filter((spell) => (spell.classes || []).map((name) => String(name).toLowerCase()).includes(classId))
    // Druidic: a Druid already has Speak with Animals prepared.
    .filter((spell) => classId !== 'druid' || spell.spellId !== SPEAK_WITH_ANIMALS_SPELL_ID);
  return {
    classId,
    catalogStatus: status,
    speciesSkill: speciesChoice,
    classSkills,
    expertise: expertiseCount ? { count: expertiseCount, options: [...granted, ...classPicks] } : null,
    rogueLanguage: classId === 'rogue' ? { options: LANGUAGE_OPTIONS.filter((language) => !known.some((value) => value.toLowerCase() === language.toLowerCase())) } : null,
    fightingStyle: FIGHTING_STYLE_CLASSES[classId] === 1 ? { options: getFightingStyleOptions(classId) } : null,
    divineOrder: classId === 'cleric' ? { options: DIVINE_ORDERS } : null,
    primalOrder: classId === 'druid' ? { options: PRIMAL_ORDERS } : null,
    mastery: mastery ? { count: mastery.count, options: mastery.eligibleWeaponKeys.map((key) => ({ key, ...(weapons[key] || Object.values(weapons).find((weapon) => normalizeWeaponKey(weapon?.name) === key) || {}) })) } : null,
    cantrips: limits.cantrips ? { count: limits.cantrips, options: classSpells.filter((spell) => Number(spell.level) === 0) } : null,
    // A Wizard writes six spells in the spellbook, then prepares from those.
    spellbook: classId === 'wizard' ? { count: 6, options: classSpells.filter((spell) => Number(spell.level) === 1) } : null,
    magicInitiate: magicInitiateLists(form).map((list) => ({ list, name: `Magic Initiate (${MAGIC_INITIATE_LISTS[list]})`, abilities: MAGIC_INITIATE_ABILITIES,
      cantrips: { count: MAGIC_INITIATE_CANTRIP_COUNT, options: catalogSpells.filter((spell) => Number(spell.level) === 0 && onList(spell, list)) },
      spells: { count: 1, options: catalogSpells.filter((spell) => Number(spell.level) === 1 && onList(spell, list)) } })),
    spells: limits.preparedSpells ? { count: limits.preparedSpells, options: classSpells.filter((spell) => Number(spell.level) === 1 && (classId !== 'wizard' || (form.classSpellbook || []).includes(spell.spellId))) } : null,
  };
}

const exactly = (picks, requirement, what) => {
  if (!requirement) return null;
  const count = Math.min(requirement.count, requirement.options.length);
  return validPicks(picks, requirement).length === count ? null : `Choose ${plural(count, what)}.`;
};

/** Wizard errors keyed by the form field that holds each choice. */
export function levelOneErrors(form = {}, requirements) {
  if (!requirements) return {};
  const errors = {};
  const add = (field, message) => { if (message) errors[field] = message; };
  add('speciesSkills', exactly(form.speciesSkills, requirements.speciesSkill, 'species skill'));
  add('classSkills', exactly(form.classSkills, requirements.classSkills, 'class skill'));
  add('classExpertise', exactly(form.classExpertise, requirements.expertise, 'Expertise skill'));
  if (requirements.rogueLanguage && !requirements.rogueLanguage.options.includes(form.rogueLanguage)) add('rogueLanguage', 'Choose a language for Thieves’ Cant.');
  if (requirements.fightingStyle && !requirements.fightingStyle.options.some((style) => style.id === form.fightingStyleId)) add('fightingStyleId', 'Choose a Fighting Style.');
  if (requirements.divineOrder && !requirements.divineOrder.options.some((order) => order.id === form.divineOrderId)) add('divineOrderId', 'Choose a Divine Order.');
  if (requirements.primalOrder && !requirements.primalOrder.options.some((order) => order.id === form.primalOrderId)) add('primalOrderId', 'Choose a Primal Order.');
  // Weapons and spells come from catalogues: wait for them, and if they could not load, leave the choice for the sheet.
  const fromCatalog = (field, requirement, what) => {
    if (requirement && !requirement.options.length) {
      if (requirements.catalogStatus !== 'failed') add(field, 'Weapons and spells are still loading.');
      return;
    }
    add(field, exactly(form[field], requirement, what));
  };
  fromCatalog('masterySelections', requirements.mastery, 'Weapon Mastery weapon');
  fromCatalog('classCantrips', requirements.cantrips, 'cantrip');
  if (requirements.spellbook) fromCatalog('classSpellbook', requirements.spellbook, 'spellbook spell');
  if (requirements.spellbook?.options.length && !requirements.spells?.options.length) add('classSpells', 'Choose your spellbook spells first.');
  else fromCatalog('classSpells', requirements.spells, 'level 1 spell');
  // Magic Initiate: one message for the feat, the first thing still missing.
  for (const feat of requirements.magicInitiate || []) {
    if (errors.magicInitiatePicks) break;
    const picks = magicInitiatePicks(form, feat.list);
    if (!feat.cantrips.options.length || !feat.spells.options.length) { if (requirements.catalogStatus !== 'failed') add('magicInitiatePicks', 'Weapons and spells are still loading.'); continue; }
    if (!feat.abilities.includes(picks.ability)) add('magicInitiatePicks', `Choose a spellcasting ability for ${feat.name}.`);
    else add('magicInitiatePicks', exactly(picks.cantrips, feat.cantrips, `${feat.name} cantrip`) || exactly(picks.spells, feat.spells, `${feat.name} level 1 spell`));
  }
  return errors;
}

/** The species skill picks folded into the race record, which the create route stores. */
export function applySpeciesSkillChoice(race, picks = [], requirement) {
  if (!race?.skillChoices) return race;
  const chosen = validPicks(picks, requirement);
  if (!chosen.length) return race;
  const next = { ...race, skills: { ...(race.skills || {}) } };
  chosen.forEach((key) => { next.skills[key] = { proficient: true }; });
  delete next.skillChoices;
  return next;
}

/**
 * Requests that save the level 1 class choices once the character exists, in order: skills before the Expertise that
 * needs them. Each uses the route the character sheet uses, so the server validates it the same way.
 */
export function levelOneFollowUps(form = {}, requirements, characterId) {
  if (!requirements || !characterId) return [];
  const id = encodeURIComponent(characterId);
  const { classId } = requirements;
  const requests = [];
  validPicks(form.classSkills, requirements.classSkills).forEach((skill) => requests.push({ label: 'class skills', where: 'Skills',
    url: `/skills/update-skills/${id}`, body: { skill, proficient: true } }));
  validPicks(form.classExpertise, requirements.expertise).forEach((skill) => requests.push({ label: 'Expertise', where: 'Skills',
    url: `/skills/update-skills/${id}`, body: { skill, proficient: true, expertise: true } }));
  if (requirements.rogueLanguage?.options.includes(form.rogueLanguage)) requests.push({ label: 'the Thieves’ Cant language', where: 'Character Overview',
    url: `/characters/${id}/classes/rogue/language`, body: { language: form.rogueLanguage } });
  if (requirements.fightingStyle?.options.some((style) => style.id === form.fightingStyleId)) requests.push({ label: 'the Fighting Style', where: 'Character Features',
    url: `/characters/${id}/classes/${classId}/fighting-style`, body: { styleId: form.fightingStyleId, expectedStyleId: null } });
  // Before the spells: a Thaumaturge's or Magician's extra cantrip needs the saved order.
  if (requirements.divineOrder?.options.some((order) => order.id === form.divineOrderId)) requests.push({ label: 'the Divine Order', where: 'Character Features',
    url: `/characters/${id}/classes/cleric/divine-order`, body: { orderId: form.divineOrderId } });
  if (requirements.primalOrder?.options.some((order) => order.id === form.primalOrderId)) requests.push({ label: 'the Primal Order', where: 'Character Features',
    url: `/characters/${id}/classes/druid/primal-order`, body: { orderId: form.primalOrderId } });
  const masteryPicks = validPicks(form.masterySelections, requirements.mastery);
  if (masteryPicks.length) requests.push({ label: 'Weapon Mastery', where: 'Character Features',
    url: `/characters/${id}/weapon-masteries/${classId}`, body: { selections: masteryPicks } });
  // Before the spells: a Wizard prepares from the saved book.
  const bookPicks = validPicks(form.classSpellbook, requirements.spellbook);
  if (bookPicks.length) requests.push({ label: 'the spellbook', where: 'Character Features', url: `/characters/${id}/classes/wizard/spellbook`, body: { spells: bookPicks } });
  // Magic Initiate's spells, saved through the route the Features card uses, which checks them against the feat.
  for (const feat of requirements.magicInitiate || []) {
    const picks = magicInitiatePicks(form, feat.list);
    const cantrips = validPicks(picks.cantrips, feat.cantrips), [spell] = validPicks(picks.spells, feat.spells);
    if (feat.abilities.includes(picks.ability) && cantrips.length === feat.cantrips.count && spell) requests.push({ label: `the ${feat.name} spells`, where: 'Character Features',
      url: `/characters/${id}/feats/magic-initiate/${feat.list}`, body: { ability: picks.ability, cantrips, spell } });
  }
  const spellIds = [...validPicks(form.classCantrips, requirements.cantrips), ...validPicks(form.classSpells, requirements.spells)];
  if (spellIds.length) requests.push({ label: 'spells', where: 'Spells',
    url: `/characters/${id}/spell-sources/class/${classId}/spells`, body: { spells: spellIds.map((spellId) => ({ spellId, source: { sourceType: 'class', sourceId: classId } })) } });
  return requests;
}

/**
 * Send the follow-up requests in order with `fetcher` (apiFetch). Stops at the first refusal and returns it, so the
 * caller can say which choice to finish on the sheet; later choices may depend on earlier ones (Expertise on skills).
 */
export async function saveLevelOneChoices(requests, fetcher) {
  for (const request of requests) {
    try {
      const response = await fetcher(request.url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request.body) });
      if (!response.ok) {
        let reason = response.statusText;
        try { reason = (await response.json())?.message || reason; } catch (_) { /* keep the status text */ }
        return { saved: false, failed: request, reason: reason || 'Request refused' };
      }
    } catch (error) {
      return { saved: false, failed: request, reason: error.message };
    }
  }
  return { saved: true };
}

export const LEVEL_ONE_FIELDS = ['speciesSkills', 'classSkills', 'classExpertise', 'rogueLanguage', 'fightingStyleId', 'divineOrderId', 'primalOrderId', 'masterySelections', 'classCantrips', 'classSpellbook', 'classSpells', 'magicInitiatePicks'];

const skillName = (key) => SKILLS.find((skill) => skill.key === key)?.label || key;

/** Review-step rows for the level 1 choices this class has; an empty value shows as missing. */
export function reviewLevelOneRows(form = {}, requirements) {
  if (!requirements) return [];
  const names = (picks, requirement, toName) => validPicks(picks, requirement).map((id) => toName(requirement.options.find((option) => optionId(option) === id) || id)).join(', ');
  const fromCatalog = (requirement, value) => (requirement && !requirement.options.length && requirements.catalogStatus === 'failed' ? 'Choose on the character sheet' : value);
  const rows = [];
  if (requirements.speciesSkill) rows.push(['Species skill', names(form.speciesSkills, requirements.speciesSkill, skillName)]);
  if (requirements.classSkills) rows.push(['Class skills', names(form.classSkills, requirements.classSkills, skillName)]);
  if (requirements.expertise) rows.push(['Expertise', names(form.classExpertise, requirements.expertise, skillName)]);
  if (requirements.rogueLanguage) rows.push(['Thieves’ Cant language', requirements.rogueLanguage.options.includes(form.rogueLanguage) ? form.rogueLanguage : '']);
  if (requirements.fightingStyle) rows.push(['Fighting Style', requirements.fightingStyle.options.find((style) => style.id === form.fightingStyleId)?.name || '']);
  if (requirements.divineOrder) rows.push(['Divine Order', requirements.divineOrder.options.find((order) => order.id === form.divineOrderId)?.name || '']);
  if (requirements.primalOrder) rows.push(['Primal Order', requirements.primalOrder.options.find((order) => order.id === form.primalOrderId)?.name || '']);
  if (requirements.mastery) rows.push(['Weapon Mastery', fromCatalog(requirements.mastery, names(form.masterySelections, requirements.mastery, (weapon) => weapon.name || weapon.key || weapon))]);
  if (requirements.cantrips) rows.push(['Cantrips', fromCatalog(requirements.cantrips, names(form.classCantrips, requirements.cantrips, (spell) => spell.name || spell))]);
  if (requirements.spellbook) rows.push(['Spellbook', fromCatalog(requirements.spellbook, names(form.classSpellbook, requirements.spellbook, (spell) => spell.name || spell))]);
  if (requirements.spells) rows.push(['Level 1 spells', fromCatalog(requirements.spells, names(form.classSpells, requirements.spells, (spell) => spell.name || spell))]);
  (requirements.magicInitiate || []).forEach((feat) => {
    const picks = magicInitiatePicks(form, feat.list);
    const chosen = [names(picks.cantrips, feat.cantrips, (spell) => spell.name || spell), names(picks.spells, feat.spells, (spell) => spell.name || spell)].filter(Boolean).join(', ');
    rows.push([feat.name, fromCatalog(feat.spells, feat.abilities.includes(picks.ability) && chosen ? `${picks.ability[0].toUpperCase()}${picks.ability.slice(1)} · ${chosen}` : '')]);
  });
  return rows;
}
